import type { SubscriptionDetails } from "schemas/generated/billing";

import React from "react";

import { useModal } from "@editor/hooks/useModal";
import { billingRouter } from "@editor/trpc/billing";

import { Card } from "@replo/design-system/components/shadcn/core/card";
import { Progress } from "@replo/design-system/components/shadcn/core/progress";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { format } from "date-fns";
import { getV2BillingPlanInfoByTier } from "schemas/billing";

import ManagePlanMenu from "../shared/ManagePlanMenu";
import {
  getCountOfV2AdditionalShopsInSubscription,
  V2PlanHeader,
} from "./V2BillingUtils";
import { V2PlanPricePerMonthIncludingAllProducts } from "./V2PlanPrice";

type V2BillingLimitsCardProps = {
  shopUsage: {
    current: number;
    maximum: number | null;
  };
  monthlyMessagesUsage: {
    current: number;
    maximum: number | null;
  };
  activeSubscription?: SubscriptionDetails;
  workspaceId: string;
  isWorkspaceOwner: boolean;
};

const V2BillingLimitsCard: React.FC<V2BillingLimitsCardProps> = ({
  shopUsage,
  monthlyMessagesUsage,
  activeSubscription,
  workspaceId,
  isWorkspaceOwner,
}) => {
  const modal = useModal();
  const { data: nextSubscriptionBillingDate } =
    billingRouter.getNextBillingDate.useQuery(
      workspaceId && activeSubscription?.id
        ? {
            workspaceId,
            subscriptionId: activeSubscription.id,
          }
        : skipToken,
    );

  const v2BillingTier = activeSubscription?.v2BillingTier ?? "free";

  const planInfo = getV2BillingPlanInfoByTier(v2BillingTier);

  const activeSubscriptionV2AdditionalShops = activeSubscription
    ? getCountOfV2AdditionalShopsInSubscription(activeSubscription)
    : 0;

  const handleCancelPlan = () => {
    if (!isWorkspaceOwner) {
      return;
    }

    modal.openModal({
      type: "cancelPlan",
      props: {
        source: "direct.billingDashboard",
        reploVersionToCancel: "v2",
      },
    });
  };

  const billingIntervalTag = activeSubscription ? (
    <Tag size="sm" variant="v2" layoutClassName="h-fit">
      {activeSubscription.billingInterval === "yearly" ? "Annual" : "Monthly"}
    </Tag>
  ) : null;

  return (
    <div className="flex w-full h-[100px]">
      <Card className="flex-1 p-4 rounded-r-none flex flex-col gap-3">
        <div className="flex justify-between items-center min-h-6">
          <span className="text-sm font-medium">Your plan</span>
          {/* NOTE (Cole 2025-10-01): Only show the manage plan menu if they're paying for v2 */}
          {activeSubscription &&
            (activeSubscription?.v2BillingTier ?? "free") !== "free" && (
              <ManagePlanMenu
                workspaceId={workspaceId}
                isWorkspaceOwner={isWorkspaceOwner}
                activeSubscription={activeSubscription}
                handleCancelPlan={handleCancelPlan}
              />
            )}
        </div>
        <V2PlanHeader tier={activeSubscription?.v2BillingTier ?? "free"} />
      </Card>
      <Card
        className={twMerge(
          "flex-1 p-4 rounded-none gap-4 border-l-0",
          activeSubscription && "gap-2",
        )}
      >
        <div className="flex justify-between items-center min-h-6">
          <span className="text-sm font-medium">Billing</span>
          {activeSubscription && v2BillingTier !== "free" && billingIntervalTag}
        </div>
        {activeSubscription ? (
          <div className="flex flex-col gap-1">
            <V2PlanPricePerMonthIncludingAllProducts
              plan={planInfo}
              billingInterval={activeSubscription.billingInterval}
              v2AdditionalShops={activeSubscriptionV2AdditionalShops}
              variant="base"
            />
            {nextSubscriptionBillingDate &&
              v2BillingTier !== "free" &&
              (!activeSubscription.endedAt ? (
                <span className="text-muted text-sm">
                  Renews{" "}
                  {format(new Date(nextSubscriptionBillingDate), "MMM d, yyyy")}
                </span>
              ) : (
                <span className="text-muted text-sm">
                  Ends{" "}
                  {format(new Date(nextSubscriptionBillingDate), "MMM d, yyyy")}
                </span>
              ))}
          </div>
        ) : (
          <span className="text-lg">$0</span>
        )}
      </Card>
      <Card className="flex-1 p-4 rounded-none flex flex-col gap-2 border-l-0">
        <span className="text-sm font-medium">Monthly chat messages</span>
        <span className="text-sm">
          <span className="header-h1">{monthlyMessagesUsage.current}</span> /{" "}
          <span>{monthlyMessagesUsage.maximum ?? "unlimited"}</span>
        </span>
        <Progress
          value={monthlyMessagesUsage.current}
          max={monthlyMessagesUsage.maximum ?? monthlyMessagesUsage.current}
          indicatorClassName="bg-gradient-to-r from-black to-[#EF196E] rounded-full"
        />
      </Card>
      <Card className="flex-1 p-4 rounded-l-none flex flex-col gap-2 border-l-0">
        <span className="text-sm font-medium">Your shops</span>
        <span className="text-sm">
          <span className="header-h1">{shopUsage.current}</span> /{" "}
          <span>{shopUsage.maximum ?? "unlimited"}</span>
        </span>
        <Progress
          value={shopUsage.current}
          max={shopUsage.maximum ?? shopUsage.current}
          indicatorClassName="bg-gradient-to-r from-black to-[#EF196E] rounded-full"
        />
      </Card>
    </div>
  );
};

export default V2BillingLimitsCard;
